import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import { BehaviorSubject, map, Observable, Subject } from "rxjs";
import { AppConfigService } from "src/app/core";
import { HelperMethods, VenueProvidedItem } from "src/app/shared";
import { EventContextService } from "../../events";
import { EventLocalStorageService } from "../../events/shared/services/event-local-storage.service";
import {
  VISIONBOARD_WORKSPACE_ITEM_STORE_SETTINGS,
  VisionboardItem,
  VisionboardService,
  VisionboardVersionsService,
  VisionboardWorkspaceItemsStoreService,
  VisionboardWorkspaceItemsStoreSettings,
} from "../../visionboard";
import { VisionboardItemResponse, VisionboardItemResponses } from "../../visionboard/responses";
import { GetVenueItemsResponse } from "../responses/venue-admin-responses";

@Injectable()
export class VenueAdminInventoryItemStoreService extends VisionboardWorkspaceItemsStoreService {
  private _destroyed$: Subject<void> = new Subject<void>();

  private selectedItemsSubject$: BehaviorSubject<VisionboardItem[]> = new BehaviorSubject<VisionboardItem[]>(null);
  selectedItems$: Observable<VisionboardItem[]> = this.selectedItemsSubject$.asObservable();

  private addedItemSubject$: Subject<VisionboardItem> = new Subject<VisionboardItem>();
  addedItem$: Observable<VisionboardItem> = this.addedItemSubject$.asObservable();

  private removedItemSubject$: Subject<VisionboardItem> = new Subject<VisionboardItem>();
  removedItem$: Observable<VisionboardItem> = this.removedItemSubject$.asObservable();

  private _selectedSkuIds: number[] = [];

  constructor(
    protected visionboardVersionService: VisionboardVersionsService,
    protected appConfigService: AppConfigService,
    protected visionboardService: VisionboardService,
    protected http: HttpClient,
    protected eventContextService: EventContextService,
    @Optional() protected eventLocalStorageService: EventLocalStorageService,
    @Optional() @Inject(VISIONBOARD_WORKSPACE_ITEM_STORE_SETTINGS) protected settings: VisionboardWorkspaceItemsStoreSettings,
  ) {
    super(visionboardVersionService, appConfigService, visionboardService, http, eventContextService, eventLocalStorageService, settings);
  }

  override userHasItem(variantSkuId: number): boolean {
    return this.userHasSelectedItem(variantSkuId);
  }

  initWithVenueId(venueId: number): Observable<boolean> {
    this.selectedItemsSubject$.next([]);

    return this.http.get<GetVenueItemsResponse>(this.appConfigService.getVenueAdminInventoryItemsUrl(venueId)).pipe(
      map(response => {
        this.selectedItemsSubject$.next(response.items.map(i => VisionboardItemResponses.getVisionboardSelectionItem(i, null)));
        this._selectedSkuIds = response.selectedSkuIds;
        return true;
      }),
    );
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  getSelectedItems(): VisionboardItem[] {
    return this.selectedItemsSubject$.value;
  }

  userHasSelectedItem(variantSkuId: number): boolean {
    return !!this._findItem(variantSkuId);
  }

  getAllItems(): VisionboardItem[] {
    return this.getSelectedItems().length ? this.getSelectedItems() : [];
  }

  toggleItem(item: VenueProvidedItem, vbItemResponse: VisionboardItemResponse, venueProvidedItems: VenueProvidedItem[]): void {
    const updatedSelectedSkuIds: number[] = venueProvidedItems.map(v => v.variantSkuId);

    if (venueProvidedItems.some(v => v.active)) {
      this._selectedSkuIds.push(...updatedSelectedSkuIds);
    } else {
      this._selectedSkuIds = this._selectedSkuIds.filter(i => !updatedSelectedSkuIds.includes(i));
    }

    if (item.active) {
      this._addItem(vbItemResponse);
    } else {
      this._removeItem(vbItemResponse);
    }
  }

  private _addItem(vbItemResponse: VisionboardItemResponse): void {
    const item = VisionboardItemResponses.getVisionboardSelectionItem(vbItemResponse, null);
    const selectedItems = this.getSelectedItems();

    if (!selectedItems.find(i => i.variantSkuId === item.variantSkuId)) {
      selectedItems.push(item);

      this.itemsSubject$.next({ store: this.getItems(), refresh: false });
      this.addedItemSubject$.next(item);
    }
  }

  private _removeItem(vbItemResponse: VisionboardItemResponse): void {
    const item = VisionboardItemResponses.getVisionboardSelectionItem(vbItemResponse, null);
    const selectedItems = this.getSelectedItems();

    const exists = selectedItems.find(i => i.variantSkuId === item.variantSkuId);

    if (exists) {
      HelperMethods.removeItem(selectedItems, exists);

      this.itemsSubject$.next({ store: this.getItems(), refresh: false });
      this.removedItemSubject$.next(item);
    }
  }

  private _findItem(variantSkuId: number): boolean {
    return this._selectedSkuIds.includes(variantSkuId);
  }
}
